/**
 * Module: `api/auth-redirect.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

const LOGIN_PATH = "/api/auth/login";
const LOGOUT_PATH = "/api/auth/logout";
export const REDIRECTING_FLAG_KEY = "__sidoni_auth_redirect_in_progress__";

let loginRedirectInFlight = false;

function getReturnTo(): string {
  if (typeof window === "undefined") {
    return "/";
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function buildOidcLoginUrl(returnTo = getReturnTo()): string {
  const loginUrl = new URL(LOGIN_PATH, window.location.origin);
  loginUrl.searchParams.set(
    "return_to",
    `${window.location.origin}${returnTo}`
  );
  return loginUrl.toString();
}

/** BFF logout (session cookie + OIDC end_session when configured). */
export function buildOidcLogoutUrl(): string {
  return new URL(LOGOUT_PATH, window.location.origin).toString();
}

export function buildOidcLoginUrlForPath(
  pathWithQueryAndHash: string,
  origin = window.location.origin
): string {
  const loginUrl = new URL(LOGIN_PATH, origin);
  loginUrl.searchParams.set("return_to", `${origin}${pathWithQueryAndHash}`);
  return loginUrl.toString();
}

/**
 * Options for TanStack `redirect()` toward BFF `/api/auth/*` endpoints.
 *
 * Same-origin absolute `href` values are rewritten to a path by
 * `router.resolveRedirect` without setting `reloadDocument`. Without an
 * explicit full-document navigation, the SPA client-navigates to
 * `/api/auth/login` (outside the root auth skip list), re-runs `beforeLoad`,
 * and hits "Too many redirects" (router-core ≥ 1.171.30).
 */
export function bffDocumentRedirect(href: string): {
  href: string;
  reloadDocument: true;
  replace: true;
} {
  return {
    href,
    reloadDocument: true,
    replace: true,
  };
}

export function markLoginRedirecting(): void {
  loginRedirectInFlight = true;
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(REDIRECTING_FLAG_KEY, "1");
  }
}

function loginAlreadyInFlight(): boolean {
  return (
    loginRedirectInFlight ||
    (typeof window !== "undefined" &&
      window.sessionStorage.getItem(REDIRECTING_FLAG_KEY) === "1")
  );
}

export function clearAuthRedirectFlag(): void {
  loginRedirectInFlight = false;
  if (typeof window === "undefined") {
    return;
  }
  window.sessionStorage.removeItem(REDIRECTING_FLAG_KEY);
}

export function redirectToOidcLogin(): void {
  if (typeof window === "undefined") {
    return;
  }

  const path = window.location.pathname;
  if (
    path.startsWith("/auth/") ||
    path.startsWith("/api/auth/") ||
    path.startsWith("/share/") ||
    path === "/beta-closed"
  ) {
    return;
  }

  if (loginAlreadyInFlight()) {
    return;
  }

  markLoginRedirecting();
  window.location.assign(buildOidcLoginUrl());
}

export function redirectToBetaClosed(): void {
  if (typeof window === "undefined") {
    return;
  }

  const path = window.location.pathname;
  if (path === "/beta-closed" || path.startsWith("/auth/")) {
    return;
  }

  window.location.assign("/beta-closed");
}

async function responseIndicatesBetaAccessDenied(
  response: Response
): Promise<boolean> {
  if (response.status !== 403) {
    return false;
  }
  const body = (await response
    .clone()
    .json()
    .catch(() => null)) as {
    error?: { type?: string };
  } | null;
  return body?.error?.type === "beta_access_denied";
}

export async function fetchWithAuthRedirect(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const response = await fetch(input, init);
  if (await responseIndicatesBetaAccessDenied(response)) {
    redirectToBetaClosed();
    return response;
  }
  if (response.status === 401) {
    redirectToOidcLogin();
  }
  return response;
}
